/**
 * Словарь оболочки: EN — источник ключей, RU и UK обязаны покрыть каждый ключ (проверяет TypeScript через `Messages`).
 * Тексты HUD, окон и вкладок держат их владельцы рядом с собой и подключают `addMessages` (./i18n.ts), чтобы
 * параллельные MR не правили один файл. Плейсхолдеры — `{name}`; формы числа — суффиксы `.one/.few/.many/.other`
 * у ключа (правила `Intl.PluralRules`), выбираются по параметру `count`.
 */
export const en = {
  "app.title": "New Fun Game",
  "app.loading": "Loading…",
  "app.startFailed": "Could not start the game. Reload the page.",
  "app.paused": "Paused",
  "app.offline": "No connection — progress will sync later",
  "app.runEnded": "You were away for over 10 minutes — the run has ended, boosts are reset.",

  "auth.loginTelegram": "Log in with Telegram",
  "auth.guest": "Guest",

  "tab.heroes": "Heroes",
  "tab.level": "Level",
  "tab.stars": "Rank",
  "tab.tech": "Tech",

  "action.continue": "Continue",
  "action.close": "Close",
  "action.claim": "Claim",
  "action.share": "Share",
  "action.retry": "Retry",

  "window.boostChoice": "Choose a boost",
  "window.bossDefeated": "Boss defeated!",
  "window.defeat": "Defeat",
  "window.welcomeBack": "Welcome back!",
  "window.streak": "Daily streak",
  "window.runEnded": "Run over",

  "wave.label": "Wave {n}",
  "coins.count.one": "{count} coin",
  "coins.count.other": "{count} coins",

  "error.bad_request": "Something went wrong. Try again.",
  "error.unauthorized": "Session expired. Reload the game.",
  "error.forbidden": "Not allowed.",
  "error.not_found": "Not found.",
  "error.conflict": "Already done.",
  "error.insufficient_coins": "Not enough coins.",
  "error.max_level": "Maximum level reached.",
  "error.locked": "Locked for now.",
  "error.wave_out_of_order": "Progress is out of sync — reloading.",
  "error.balance_version_unsupported": "A new version is out — reloading.",
  "error.rate_limited": "Too fast. Wait a moment.",
  "error.internal": "Server error. Try again later.",
  "error.network": "No connection.",
} as const;

/** Ключ словаря оболочки. */
export type CoreKey = keyof typeof en;
/** Формы числа (`Intl.PluralRules`). */
export type PluralForm = "zero" | "one" | "two" | "few" | "many" | "other";
/**
 * Полный словарь одного языка: все ключи `K` плюс дополнительные формы числа, которых нет в EN
 * (в русском у `coins.count` есть `.few` и `.many`).
 */
export type Messages<K extends string = CoreKey> = Record<K, string> & { [plural: `${string}.${PluralForm}`]: string };

export const ru: Messages = {
  "app.title": "New Fun Game",
  "app.loading": "Загрузка…",
  "app.startFailed": "Игра не запустилась. Перезагрузите страницу.",
  "app.paused": "Пауза",
  "app.offline": "Нет связи — прогресс сохранится позже",
  "app.runEnded": "Вас не было больше 10 минут — забежка закончилась, усиления сброшены.",

  "auth.loginTelegram": "Войти через Telegram",
  "auth.guest": "Гость",

  "tab.heroes": "Герои",
  "tab.level": "Уровень",
  "tab.stars": "Ранг",
  "tab.tech": "Техника",

  "action.continue": "Дальше",
  "action.close": "Закрыть",
  "action.claim": "Забрать",
  "action.share": "Поделиться",
  "action.retry": "Повторить",

  "window.boostChoice": "Выберите усиление",
  "window.bossDefeated": "Босс повержен!",
  "window.defeat": "Поражение",
  "window.welcomeBack": "С возвращением!",
  "window.streak": "Серия дней",
  "window.runEnded": "Забежка окончена",

  "wave.label": "Волна {n}",
  "coins.count.one": "{count} монета",
  "coins.count.few": "{count} монеты",
  "coins.count.many": "{count} монет",
  "coins.count.other": "{count} монеты",

  "error.bad_request": "Что-то пошло не так. Попробуйте ещё раз.",
  "error.unauthorized": "Сессия истекла. Перезапустите игру.",
  "error.forbidden": "Нельзя.",
  "error.not_found": "Не найдено.",
  "error.conflict": "Уже сделано.",
  "error.insufficient_coins": "Не хватает монет.",
  "error.max_level": "Максимальный уровень.",
  "error.locked": "Пока закрыто.",
  "error.wave_out_of_order": "Прогресс разошёлся — перезагружаем.",
  "error.balance_version_unsupported": "Вышла новая версия — перезагружаем.",
  "error.rate_limited": "Слишком быстро. Подождите немного.",
  "error.internal": "Ошибка сервера. Попробуйте позже.",
  "error.network": "Нет связи.",
};

export const uk: Messages = {
  "app.title": "New Fun Game",
  "app.loading": "Завантаження…",
  "app.startFailed": "Гра не запустилася. Перезавантажте сторінку.",
  "app.paused": "Пауза",
  "app.offline": "Немає зв’язку, прогрес збережеться пізніше",
  "app.runEnded": "Вас не було понад 10 хвилин: забіг закінчився, підсилення скинуто.",

  "auth.loginTelegram": "Увійти через Telegram",
  "auth.guest": "Гість",

  "tab.heroes": "Герої",
  "tab.level": "Рівень",
  "tab.stars": "Ранг",
  "tab.tech": "Техніка",

  "action.continue": "Далі",
  "action.close": "Закрити",
  "action.claim": "Забрати",
  "action.share": "Поділитися",
  "action.retry": "Повторити",

  "window.boostChoice": "Оберіть підсилення",
  "window.bossDefeated": "Боса розбито!",
  "window.defeat": "Поразка",
  "window.welcomeBack": "З поверненням!",
  "window.streak": "Серія днів",
  "window.runEnded": "Забіг закінчено",

  "wave.label": "Хвиля {n}",
  "coins.count.one": "{count} монета",
  "coins.count.few": "{count} монети",
  "coins.count.many": "{count} монет",
  "coins.count.other": "{count} монети",

  "error.bad_request": "Щось пішло не так. Спробуйте ще раз.",
  "error.unauthorized": "Сесія закінчилася. Перезапустіть гру.",
  "error.forbidden": "Не можна.",
  "error.not_found": "Не знайдено.",
  "error.conflict": "Уже зроблено.",
  "error.insufficient_coins": "Бракує монет.",
  "error.max_level": "Максимальний рівень.",
  "error.locked": "Поки що закрито.",
  "error.wave_out_of_order": "Прогрес розійшовся, перезавантажуємо.",
  "error.balance_version_unsupported": "Вийшла нова версія, перезавантажуємо.",
  "error.rate_limited": "Занадто швидко. Зачекайте трохи.",
  "error.internal": "Помилка сервера. Спробуйте пізніше.",
  "error.network": "Немає зв’язку.",
};
